import React, { useState } from "react";
import tw from "twin.macro";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts";

const Wrap = tw.div`max-w-3xl mx-auto`;
const Heading = tw.h1`text-4xl md:text-5xl font-black text-primary-900 text-center mb-12 leading-snug`;
const Form = tw.form`mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4`;
const Full = tw.div`sm:col-span-2`;
const Label = tw.label`block text-sm font-medium text-gray-700 mb-1`;
const Input = tw.input`w-full px-4 py-3 rounded-3xl bg-white border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500`;
const Textarea = tw.textarea`w-full px-4 py-3 rounded-3xl bg-white border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 h-40`;
const Row = tw.div`flex items-center gap-2 text-sm text-gray-600`;
const Checkbox = tw.input`w-4 h-4`;
const Button = tw.button`px-6 py-3 rounded-3xl bg-primary-500 text-white font-semibold hover:bg-primary-700 transition disabled:opacity-60`;
const Note = tw.p`mt-2 text-xs text-gray-500`;
const SuccessBox = tw.div`rounded-2xl bg-green-50 border border-green-200 p-8 text-center`;
const SuccessHeading = tw.h2`text-2xl font-bold text-green-800 mb-2`;
const SuccessText = tw.p`text-green-700`;
const ErrorMsg = tw.p`text-red-600 text-sm mt-2`;

// Points to the EthosHub Next.js app's /api/contact route.
// Set REACT_APP_CONTACT_API_URL in Amplify environment variables once the app is deployed.
const API_URL = process.env.REACT_APP_CONTACT_API_URL
  ? `${process.env.REACT_APP_CONTACT_API_URL}/api/contact`
  : '/api/contact'

export default function ContactForm({ title = "Contact Us" }) {
  const [fields, setFields] = useState({
    firstName: '', lastName: '', email: '', company: '',
    employees: '', website: '', message: '',
  })
  const [consent, setConsent] = useState(false)
  const [honeypot, setHoneypot] = useState('')   // bots fill this; humans never see it
  const [status, setStatus] = useState('idle') // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState('')

  const set = (k, v) => setFields(prev => ({ ...prev, [k]: v }))

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!consent) {
      setErrorMsg('Please accept our privacy notice.')
      return
    }
    setErrorMsg('')
    setStatus('sending')
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...fields, source: 'contact', website_url: honeypot }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Submission failed')
      setStatus('sent')
    } catch (err) {
      setErrorMsg(err.message || 'Something went wrong. Please try again.')
      setStatus('error')
    }
  }

  if (status === 'sent') {
    return (
      <Container>
        <ContentWithPaddingXl>
          <Wrap>
            <SuccessBox>
              <SuccessHeading>Thanks for getting in touch.</SuccessHeading>
              <SuccessText>We've received your message and will get back to you shortly.</SuccessText>
            </SuccessBox>
          </Wrap>
        </ContentWithPaddingXl>
      </Container>
    )
  }

  return (
    <Container>
      <ContentWithPaddingXl>
        <Wrap>
          <Heading>{title}</Heading>

          <Form onSubmit={onSubmit} noValidate>
            <div>
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName" type="text" placeholder="Jane" required maxLength={40}
                value={fields.firstName} onChange={e => set('firstName', e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName" type="text" placeholder="Doe" required maxLength={80}
                value={fields.lastName} onChange={e => set('lastName', e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email" type="email" placeholder="jane@company.com" required maxLength={100}
                value={fields.email} onChange={e => set('email', e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="company">Company *</Label>
              <Input
                id="company" type="text" placeholder="Acme Ltd" required maxLength={200}
                value={fields.company} onChange={e => set('company', e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="employees">Number of Employees *</Label>
              <Input
                id="employees" type="number" placeholder="e.g. 150" required min={1}
                value={fields.employees} onChange={e => set('employees', e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="website">Website</Label>
              <Input
                id="website" type="url" placeholder="https://yourcompany.com" maxLength={255}
                value={fields.website} onChange={e => set('website', e.target.value)}
              />
            </div>

            <Full>
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message" placeholder="Tell us a bit about your needs..."
                value={fields.message} onChange={e => set('message', e.target.value)}
              />
            </Full>

            <Full>
              {/* Honeypot: hidden from humans, bots fill it and the server drops the submission */}
              <input type="text" name="website_url" tabIndex={-1} autoComplete="off" aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1 }}
                value={honeypot} onChange={e => setHoneypot(e.target.value)} />

              <Row>
                <Checkbox
                  id="contact-consent" type="checkbox"
                  checked={consent} onChange={e => setConsent(e.target.checked)}
                />
                <label htmlFor="contact-consent">
                  I agree to the{" "}
                  <a href="/privacy-policy" className="underline text-primary-600">Privacy Policy</a>{" "}
                  and{" "}
                  <a href="/terms-of-service" className="underline text-primary-600">Terms of Service</a>.
                </label>
              </Row>
              <Note>We'll only use your details to respond to your enquiry.</Note>
              {errorMsg && <ErrorMsg>{errorMsg}</ErrorMsg>}
            </Full>

            <Full>
              <Button type="submit" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending…' : 'Send Message'}
              </Button>
            </Full>
          </Form>
        </Wrap>
      </ContentWithPaddingXl>
    </Container>
  )
}
